import { Box, Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Artist, ServerListResponse } from "../../../types";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { apiClient } from "../../../services/axiosInstance";
import { artistResultsCardV2 } from "../../../components/resultsGrid/ArtistsResultsCard";
import { FluidVirtualGrid } from "../../../components/resultsGrid/FluidVirtualGrid";
import { sizeEstimator } from "../../../components/cards/hooks/useGridCardDimensions";
import { throttle } from "lodash-es";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";
import { useOutletContext } from "react-router";
import { ShelfOutletContext } from "../MyShelfScreen";
const QUERY_KEY = ["followed-creators"];
const PER_PAGE = 24;

export default function CreatorsShelfScreen() {
  const [t] = useTranslation();
  const topBarHeight = useTopBarHeight();

  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [...QUERY_KEY, searchQuery],
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await apiClient.get<ServerListResponse<Artist>>(
          "/v1/account/followed-creators",
          {
            params: {
              p: pageParam,
              perpage: PER_PAGE,
              q: searchQuery || undefined,
            },
            signal,
          },
        );
        return response;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const pagination = lastPage.data.payload.pagination;
        if (!pagination) return undefined;
        const nextPage = allPages.length + 1;
        return nextPage <= pagination.total_pages ? nextPage : undefined;
      },
    });

  const queryClient = useQueryClient();
  const allPages = useMemo(() => {
    data?.pages.forEach((page) => {
      page.data.payload.results.forEach((artist: Artist) => {
        const cacheKey = `${artist.entity_type}-${artist.id}`;
        queryClient.setQueryData([cacheKey], artist);
      });
    });
    return (data?.pages ?? []) as AxiosResponse<ServerListResponse<Artist>>[];
  }, [data, queryClient]);

  const throttleSearch = useCallback(
    throttle((term: string) => {
      setSearchQuery(term);
    }, 300),
    [],
  );

  // Register search config with parent shelf tabs — runs on every mount
  // (deps are stable across remounts, so useEffect would skip re-registration)
  const { setSearchConfig } = useOutletContext<ShelfOutletContext>();
  useLayoutEffect(() => {
    setSearchConfig({
      placeholder: t("screens.shelf.creatorsSearchPlaceholder"),
      onSearch: throttleSearch,
    });
  });

  const layout = artistResultsCardV2("grid");
  const dims = sizeEstimator(
    layout.style.context,
    layout.style.mode,
    layout.style.size,
  );

  return (
    <Flex
      direction="column"
      width="full"
      sx={{ WebkitOverflowScrolling: "touch" }}
    >
      <FluidVirtualGrid
        gridKey="shelf-creators"
        header={<Box pt="24px" />}
        gridHeightAdjustment={`${topBarHeight.get()}px`}
        layout={layout}
        minCardWidth={dims.width}
        minColumns={2}
        gap={24}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        allPages={allPages}
      />
    </Flex>
  );
}
